// pages/admin/ProjectDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  DollarSign,
  Clock,
  Briefcase,
  CheckCircle,
  XCircle,
  Loader2,
  MoreVertical,
  Eye,
} from "lucide-react";
import {
  useGetAdminProjectDetails,
  useGetProjectAssignments,
  useDeleteProject,
  useReviewApplication,
  useUpdateUserProjectStatus,
  useUnassignProject,
} from "../../lib/api/adminProjects";
import { UserAssignment } from "../../lib/types/adminProjects";

const ProjectDetails: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<UserAssignment | null>(null);
  const [actionType, setActionType] = useState<string | null>(null);
  const [actionReason, setActionReason] = useState("");

  // Fetch project details
  const { data: projectData, isLoading: projectLoading } =
    useGetAdminProjectDetails(projectId);
  const project = projectData?.data;

  // Fetch assignments
  const { data: assignmentsData, isLoading: assignmentsLoading } =
    useGetProjectAssignments(projectId, { status: statusFilter || undefined });
  const assignments = assignmentsData?.data?.assignments || [];

  // Mutations
  const { mutateAsync: deleteProject, isPending: isDeleting } =
    useDeleteProject();
  const { mutateAsync: reviewApplication, isPending: isReviewing } =
    useReviewApplication();
  const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
    useUpdateUserProjectStatus();
  const { mutateAsync: unassignProject, isPending: isUnassigning } =
    useUnassignProject();

  const getCategoryConfig = (category: string) => {
    const configs: Record<string, { bg: string; text: string }> = {
      "AI Training": { bg: "bg-blue-100", text: "text-blue-700" },
      "Data Annotation": { bg: "bg-emerald-100", text: "text-emerald-700" },
      "Code Review": { bg: "bg-purple-100", text: "text-purple-700" },
      "Content Moderation": { bg: "bg-amber-100", text: "text-amber-700" },
      "Audio Transcription": { bg: "bg-rose-100", text: "text-rose-700" },
      "Sentiment Analysis": { bg: "bg-cyan-100", text: "text-cyan-700" },
      "Prompt Engineering": { bg: "bg-indigo-100", text: "text-indigo-700" },
      "Image Classification": { bg: "bg-teal-100", text: "text-teal-700" },
      "Text Review": { bg: "bg-orange-100", text: "text-orange-700" },
      "Video Annotation": { bg: "bg-pink-100", text: "text-pink-700" },
    };
    return configs[category] || { bg: "bg-gray-100", text: "text-gray-700" };
  };

  const getDifficultyConfig = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return { color: "text-emerald-600", bg: "bg-emerald-100", bars: 1 };
      case "Medium":
        return { color: "text-amber-600", bg: "bg-amber-100", bars: 2 };
      case "Hard":
        return { color: "text-red-600", bg: "bg-red-100", bars: 3 };
      default:
        return { color: "text-gray-600", bg: "bg-gray-100", bars: 0 };
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; icon: any }> = {
      assigned: { bg: "bg-blue-50", text: "text-blue-700", icon: Clock },
      pending: { bg: "bg-amber-50", text: "text-amber-700", icon: Clock },
      approved: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        icon: CheckCircle,
      },
      rejected: { bg: "bg-red-50", text: "text-red-700", icon: XCircle },
      active: { bg: "bg-green-50", text: "text-green-700", icon: CheckCircle },
      completed: {
        bg: "bg-purple-50",
        text: "text-purple-700",
        icon: CheckCircle,
      },
      terminated: { bg: "bg-gray-100", text: "text-gray-600", icon: XCircle },
    };
    return (
      configs[status] || {
        bg: "bg-gray-100",
        text: "text-gray-600",
        icon: Clock,
      }
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDelete = async () => {
    try {
      await deleteProject(projectId!);
      navigate("/admin/projects");
    } catch {
      // Error handled by mutation
    }
  };

  const handleAction = async () => {
    if (!selectedAssignment || !actionType) return;

    try {
      switch (actionType) {
        case "approve":
          await reviewApplication({
            userProjectId: selectedAssignment._id,
            payload: { action: "approve" },
          });
          break;
        case "reject":
          await reviewApplication({
            userProjectId: selectedAssignment._id,
            payload: { action: "reject", rejectionReason: actionReason },
          });
          break;
        case "activate":
          await updateStatus({
            userProjectId: selectedAssignment._id,
            payload: { status: "active" },
          });
          break;
        case "complete":
          await updateStatus({
            userProjectId: selectedAssignment._id,
            payload: { status: "completed" },
          });
          break;
        case "terminate":
          await updateStatus({
            userProjectId: selectedAssignment._id,
            payload: { status: "terminated", reason: actionReason },
          });
          break;
        case "unassign":
          await unassignProject({
            userProjectId: selectedAssignment._id,
            reason: actionReason,
          });
          break;
      }
      setSelectedAssignment(null);
      setActionType(null);
      setActionReason("");
    } catch {
      // Error handled by mutation
    }
  };

  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openMenuId]);

  if (projectLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Project Not Found
          </h2>
          <button
            onClick={() => navigate("/admin/projects")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B52FF] text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const categoryConfig = getCategoryConfig(project.category);
  const difficultyConfig = getDifficultyConfig(project.difficulty);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/admin/projects")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${categoryConfig.bg} ${categoryConfig.text}`}
              >
                {project.category}
              </span>
              {!project.isActive && (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  Inactive
                </span>
              )}
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              {project.title}
            </h1>
            <p className="text-gray-500 mt-1">{project.projectId}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/admin/projects/${projectId}/edit`)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => setDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Stats Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Project Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span>Hourly Rate</span>
                </div>
                <span className="font-semibold text-gray-900">
                  ${project.hourlyRate}/hr
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Est. Hours</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {project.estimatedHours}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Briefcase className="w-4 h-4" />
                  <span>Difficulty</span>
                </div>
                <span className={`font-semibold ${difficultyConfig.color}`}>
                  {project.difficulty}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>Slots</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {project.filledSlots}/{project.totalSlots}
                </span>
              </div>
            </div>

            {/* Slots Progress */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Capacity</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round((project.filledSlots / project.totalSlots) * 100)}
                  %
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-full"
                  style={{
                    width: `${(project.filledSlots / project.totalSlots) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Assignment Stats */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Assignment Breakdown
            </h3>
            <div className="space-y-3">
              {[
                {
                  label: "Assigned",
                  count: project.stats?.assignedCount || 0,
                  color: "bg-blue-500",
                },
                {
                  label: "Pending",
                  count: project.stats?.pendingCount || 0,
                  color: "bg-amber-500",
                },
                {
                  label: "Active",
                  count: project.stats?.activeCount || 0,
                  color: "bg-emerald-500",
                },
                {
                  label: "Completed",
                  count: project.stats?.completedCount || 0,
                  color: "bg-purple-500",
                },
                {
                  label: "Rejected",
                  count: project.stats?.rejectedCount || 0,
                  color: "bg-red-500",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${stat.color}`} />
                    <span className="text-sm text-gray-600">{stat.label}</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {stat.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {project.description}
            </p>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-3">
              Skills Required
            </h3>
            <div className="flex flex-wrap gap-2">
              {project.skillsRequired?.map((skill) => (
                <span
                  key={skill.name}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    skill.required
                      ? "bg-gray-100 text-gray-700"
                      : "bg-gray-50 text-gray-500"
                  }`}
                >
                  {skill.name}
                  {!skill.required && " (Optional)"}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Assignments Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Assignments
                  </h2>
                  <p className="text-sm text-gray-500">
                    Users assigned to this project
                  </p>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="assigned">Assigned</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Assignments List */}
            <div className="divide-y divide-gray-50">
              {assignmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
                </div>
              ) : assignments.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No assignments
                  </h3>
                  <p className="text-gray-500">
                    No users have been assigned to this project yet
                  </p>
                </div>
              ) : (
                assignments.map((assignment) => {
                  const statusConfig = getStatusConfig(assignment.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div
                      key={assignment._id}
                      className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white font-medium flex-shrink-0">
                            {assignment.userId.firstname?.[0]}
                            {assignment.userId.lastname?.[0]}
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-semibold text-gray-900">
                                {assignment.userId.firstname}{" "}
                                {assignment.userId.lastname}
                              </h3>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                              >
                                <StatusIcon className="w-3.5 h-3.5" />
                                {assignment.status.charAt(0).toUpperCase() +
                                  assignment.status.slice(1)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {assignment.userId.email}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Assigned {formatDate(assignment.assignedAt)}
                              {assignment.appliedAt &&
                                ` • Applied ${formatDate(assignment.appliedAt)}`}
                            </p>
                          </div>
                        </div>

                        {/* Actions Button */}
                        <button
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setMenuPosition({
                              top: rect.bottom + 4,
                              left: rect.right - 192,
                            });
                            setOpenMenuId(
                              openMenuId === assignment._id
                                ? null
                                : assignment._id
                            );
                          }}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Fixed Position Dropdown Menu - Rendered outside the container */}
        {openMenuId && menuPosition && (
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setOpenMenuId(null);
                setMenuPosition(null);
              }}
            />
            <div
              className="fixed w-48 bg-white rounded-xl border border-gray-200 shadow-lg z-[101] py-1"
              style={{
                top: menuPosition.top,
                left: Math.max(8, menuPosition.left),
              }}
            >
              {(() => {
                const assignment = assignments.find(
                  (a) => a._id === openMenuId
                );
                if (!assignment) return null;

                return (
                  <>
                    <button
                      onClick={() => {
                        navigate(`/admin/users/${assignment.userId._id}`);
                        setOpenMenuId(null);
                        setMenuPosition(null);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4" />
                      View User
                    </button>

                    {assignment.status === "pending" && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setActionType("approve");
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setActionType("reject");
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}

                    {assignment.status === "approved" && (
                      <button
                        onClick={() => {
                          setSelectedAssignment(assignment);
                          setActionType("activate");
                          setOpenMenuId(null);
                          setMenuPosition(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark Active
                      </button>
                    )}

                    {assignment.status === "active" && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setActionType("complete");
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark Completed
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setActionType("terminate");
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Terminate
                        </button>
                      </>
                    )}

                    {["assigned", "rejected"].includes(assignment.status) && (
                      <>
                        <hr className="my-1 border-gray-100" />
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setActionType("unassign");
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Unassign
                        </button>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(false)}
          />
          <div className="relative bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Project?
            </h3>
            <p className="text-gray-500 mb-6">
              This action cannot be undone. Projects with active assignments
              cannot be deleted.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {selectedAssignment && actionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setSelectedAssignment(null);
              setActionType(null);
              setActionReason("");
            }}
          />
          <div className="relative bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {actionType === "approve" && "Approve Application"}
              {actionType === "reject" && "Reject Application"}
              {actionType === "activate" && "Mark as Active"}
              {actionType === "complete" && "Mark as Completed"}
              {actionType === "terminate" && "Terminate Assignment"}
              {actionType === "unassign" && "Unassign Project"}
            </h3>
            <p className="text-gray-500 mb-4">
              {actionType === "approve" &&
                "The user will be notified and can start working on this project."}
              {actionType === "reject" &&
                "Please provide a reason for rejection."}
              {actionType === "activate" &&
                "This will mark the user as actively working."}
              {actionType === "complete" &&
                "This will mark the project as completed."}
              {actionType === "terminate" &&
                "Please provide a reason for termination."}
              {actionType === "unassign" &&
                "This will remove the project from the user."}
            </p>

            {["reject", "terminate"].includes(actionType) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] resize-none"
                />
              </div>
            )}

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setSelectedAssignment(null);
                  setActionType(null);
                  setActionReason("");
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={
                  isReviewing ||
                  isUpdatingStatus ||
                  isUnassigning ||
                  (["reject", "terminate"].includes(actionType) &&
                    !actionReason)
                }
                className={`px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2 ${
                  ["reject", "terminate", "unassign"].includes(actionType)
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-[#8B52FF] text-white hover:bg-[#7040E0]"
                }`}
              >
                {(isReviewing || isUpdatingStatus || isUnassigning) && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
